/**
 * Client-side CSV export for a table extracted from an agent answer
 * (see markdown.js's extractTableFromMarkdown). No backend involved — this
 * builds a CSV Blob from exactly what's already rendered on screen and
 * triggers a browser download, so the exported file can never drift from
 * what the user actually sees in the table.
 */

function csvEscapeCell(value) {
  const str = String(value ?? "");
  // Quote (and double-up internal quotes) whenever a cell contains a comma,
  // quote, or newline — standard CSV escaping (RFC 4180).
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function buildCsv(headers, rows) {
  const lines = [headers, ...rows].map((row) => row.map(csvEscapeCell).join(","));
  return lines.join("\r\n");
}

export function downloadCsv(filename, headers, rows) {
  const csvContent = buildCsv(headers, rows);
  // Leading BOM so Excel (still the most likely consumer here) opens the
  // file as UTF-8 rather than guessing at an ANSI codepage and mangling
  // anything non-ASCII in a site name, trainee status, etc.
  const blob = new Blob(["\ufeff", csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generic "download this text as a file" helper -- same Blob + anchor-click
 * mechanism as downloadCsv above, factored out so the conversation-export
 * feature doesn't duplicate it for a plain-text transcript.
 */
export function downloadTextFile(filename, content, mimeType = "text/plain;charset=utf-8;") {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
